/**
 * Overall course progress gauge.
 */
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import highcharts3d from "highcharts/highcharts-3d";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { VText } from "form";
import { useAsterController } from "context";
import { calcProgressValue, mapSectionToGradeDetails } from "utils/grade.util";

highcharts3d(Highcharts);

/**
 * Displays weighted course progress as a circular indicator.
 */
const AnalysisCourseTotalProgress = () => {

  const { t } = useTranslation('common');

  const { courseId } = useParams();

  const [controller] = useAsterController();
  const { miniSidenav, courses, themes } = controller;

  const currCourse = useMemo(() => (courses.data || []).find(v => v.id === courseId) || {}, [courses, courseId]);

  const [coord, setCoord] = useState({ width: 0, height: 0 });
  const [show, setShow] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [total, setTotal] = useState(0);
  const COLORS = useMemo(() => [
    (themes || []).find(v => v.name === 'color_main1')?.value || '',
    (themes || []).find(v => v.name === 'color_main2')?.value || '',
    (themes || []).find(v => v.name === 'color_main3')?.value || '',
  ] || [], [themes]);

  useEffect(() => {
    const grade = calcProgressValue(currCourse);
    setTotal(Math.round(grade * 100) / 100);
    const mapper = mapSectionToGradeDetails(currCourse).filter(m => m['details'] && m['cutoff'] !== 0);

    const dataSeries = [...mapper.map((m, idx) => ({
      name: m['sectionTitle'],
      y: Math.round((m['percent'] * 100)) / 100.0,
      color: COLORS[idx % 3]
    })), {
      name: "Not Earned",
      y: Math.round((100 - grade) * 100) / 100.0,
      color: COLORS[1]
    }];

    setChartData(dataSeries);
  }, [currCourse, COLORS]);

  useEffect(() => { setShow(false); setTimeout(() => setShow(true), 300); }, [miniSidenav]);

  const chartRef = useCallback((node) => {
    if (node) {
      setCoord({
        width: node.chart?.plotWidth + node.chart?.plotLeft,
        height: node.chart?.plotHeight + node.chart?.plotTop * 2,
      });
    }
  }, []);

  const chartOption = useMemo(() => ({
    chart: {
      type: "pie",
      backgroundColor: "rgba(0, 0, 0, 0)",
      height: 280
    },
    title: {
      text: "",
      floating: true
    },
    plotOptions: {
      pie: {
        showInLegend: true,
        innerSize: "80%",
        dataLabels: { enabled: false },
        borderWidth: 0
      }
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        type: "pie",
        name: "%",
        data: chartData
      }
    ],
    credits: {
      enabled: false
    }
  }), [chartData]);

  return <Box className="border border-solid border-gray-300 dark:border-gray-500 h-full rounded-md px-6 pt-6">
    <VText className="text-xl">{t("Total Progress")}</VText>
    <Box className="flex justify-center max-h-72 overflow-y-auto h-full">
      <Box className="w-full h-full">
        {
          chartOption &&
          <Box className="relative h-full">
            <Box className="w-full absolute left-0 top-0 flex flex-col justify-center items-center" sx={{ width: coord.width, height: coord.height }}>
              <VText className="text-4xl">{total}%</VText>
            </Box>
            {
              show &&
              <HighchartsReact
                ref={chartRef}
                containerProps={{ style: { display: 'flex', justifyContent: 'center' } }}
                highcharts={Highcharts}
                options={chartOption}
              />
            }
          </Box>
        }
      </Box>
    </Box>
  </Box>;
};

export default AnalysisCourseTotalProgress;